import { getSupabase } from "../supabase/client";
import { normalizeKpis, deepMerge } from "../kpis";
import type {
  ConsultingRequestInput,
  ConsultingRequest,
  ConsultingRequestResult,
  UUID,
} from "./types";
import type { Kpis } from "../kpis/defaults";
import { ICurrency } from "@/types";
import { Deal } from "@/types/deal";

type ProjectRow = {
  id: string;
  name: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_edited_by?: string | null;
  kpis?: Record<string, any> | null;
  stage?: string | null;
  currency?: ICurrency | null;
  property_id?: string | null;
};

type SaveArgs = {
  id?: string;
  name: string;
  property_id?: string | null;
  stage?: string | null;
  currency?: string | null;
  kpis?: Record<string, any> | null;
};

export type IntroRequestInput = {
  name?: string;
  email: string;
  message?: string;
};

export type IntroRequestResult = {
  id: string;
  created_at: string;
};

export interface DeletionRequestInput {
  userId: string;
}

export interface DeletionRequestResult {
  id: string;
  created_at: string;
  token: string;
  expires_at: string;
}

export async function init() {
  return;
}

export async function propertyExists(id: string): Promise<boolean> {
  const sb = getSupabase();
  const { data, error } = await sb
    .from("properties")
    .select("id")
    .eq("id", id)
    .maybeSingle();
  if (error && error.code !== "PGRST116") throw error;
  return !!data;
}

export async function listProjects(): Promise<ProjectRow[]> {
  const supabase = getSupabase();

  const { data: authData, error: authError } = await supabase.auth.getSession();
  const userId = authData.session?.user?.id;

  if (authError || !userId) {
    console.warn(
      "listProjects called without an active authenticated user session.",
    );
    return [];
  }

  const { data, error } = await supabase
    .from("projects")
    .select(
      "id,name,created_at,updated_at,last_edited_by,kpis,stage,currency,property_id",
    )
    .eq("owner_id", userId)
    .order("created_at", { ascending: false });

  if (error) throw error;

  const rows = (data ?? []) as ProjectRow[];
  rows.forEach((r) => {
    r.kpis = normalizeKpis(r.kpis);
  });

  return rows;
}

export async function saveProject(args: SaveArgs): Promise<ProjectRow> {
  const supabase = getSupabase();

  const projectName = args.name?.trim() || "Untitled Project";

  let propertyIdForSave: string | null = null;
  if (args.property_id) {
    try {
      if (await propertyExists(args.property_id)) {
        propertyIdForSave = args.property_id;
      }
    } catch (e) {
      console.warn("[saveProject] Property existence check failed:", e);
    }
  }

  let kpisPayload: Record<string, any> | null = args.kpis ?? null;
  let currencyForSave: string | null = args.currency ?? null;

  if (!kpisPayload && !args.id && args.property_id) {
    console.info(
      "[saveProject] 🆕 New project - loading template KPIs from property:",
      args.property_id,
    );
    try {
      const tmpl = await getPropertyTemplateKpis(args.property_id);
      if (tmpl) {
        kpisPayload = normalizeKpis(tmpl as any);
        if (!currencyForSave && (tmpl as any)?.currency) {
          currencyForSave = (tmpl as any).currency;
        }
      } else {
        console.warn(
          "[saveProject] ⚠️  No template KPIs found for property:",
          args.property_id,
        );
      }
    } catch (e) {
      console.warn("[saveProject] ❌ Template KPIs load failed:", e);
    }
  } else if (!kpisPayload && args.id) {
    console.error(
      "[saveProject] 🚨 WARNING: Updating existing project without KPIs!",
      {
        projectId: args.id,
        propertyId: args.property_id,
        message:
          "This will overwrite user data with empty/template values. This is likely a bug in the calling code.",
      },
    );
  }

  const projectData: any = {
    name: projectName,
    property_id: propertyIdForSave,
    stage: args.stage ?? "underwriting",
    currency: currencyForSave ?? "EUR",
    kpis: kpisPayload ?? {},
  };

  if (args.id) {
    projectData.id = args.id;
  }

  const { data, error } = await supabase
    .from("projects")
    .upsert(projectData, {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select()
    .single();

  if (error) {
    console.error("[saveProject] Upsert failed:", error);
    throw error;
  }

  const row = data as ProjectRow;
  row.kpis = normalizeKpis(row.kpis);
  return row;
}

export async function getProject(id: string): Promise<ProjectRow | null> {
  const supabase = getSupabase();

  try {
    const { data, error } = await supabase
      .from("projects")
      .select("id,name,stage,currency,kpis,created_at,updated_at,property_id")
      .eq("id", id)
      .maybeSingle();

    if (error && error.code !== "PGRST116") {
      throw error;
    }

    if (!data) {
      return null;
    }

    const row = data as ProjectRow;
    row.kpis = normalizeKpis(row.kpis);
    return row;
  } catch (error) {
    console.error("[getProject] Failed to fetch project:", error);
    throw error;
  }
}

export async function getProjecById(id: string): Promise<ProjectRow | null> {
  const supabase = getSupabase();

  try {
    const { data, error } = await supabase
      .from("projects")
      .select("id,name,stage,currency,kpis,created_at,updated_at,property_id")
      .eq("id", id)
      .maybeSingle();

    if (error && error.code !== "PGRST116") {
      throw error;
    }

    if (!data) {
      return null;
    }

    const row = data as ProjectRow;
    row.kpis = normalizeKpis(row.kpis);
    return row;
  } catch (error) {
    console.error("[getProject] Failed to fetch project:", error);
    throw error;
  }
}

export async function deleteProject(id: string): Promise<void> {
  const sb = getSupabase();

  try {
    await sb.from("roadmap_tasks").delete().eq("project_id", id);
  } catch {}

  const { error } = await sb.from("projects").delete().eq("id", id);
  if (error) throw error;
}

export async function patchProjectKpis(id: string, patch: Record<string, any>) {
  const supabase = getSupabase();

  try {
    const current = await getProject(id);
    if (!current) {
      throw new Error(`Project ${id} not found`);
    }

    const merged = deepMerge(current.kpis || {}, patch || {});

    const { data, error } = await supabase
      .from("projects")
      .update({ kpis: merged })
      .eq("id", id)
      .select("id,kpis,updated_at")
      .single();

    if (error) throw error;
    if (data) (data as any).kpis = normalizeKpis((data as any).kpis);
    return data;
  } catch (error) {
    console.error("[patchProjectKpis] Failed:", error);
    throw error;
  }
}

export async function createConsultingRequest(
  input: ConsultingRequestInput,
): Promise<{ data?: ConsultingRequestResult; error?: string | null }> {
  const supabase = getSupabase();
  const row = {
    name: input.name ?? null,
    email: input.email ?? null,
    expertise: input.expertise,
    seniority: input.seniority,
    estimated_hours: input.estimatedHours ?? null,
    message: input.message ?? null,
  };

  const { data, error } = await supabase
    .from("consulting_requests")
    .insert(row)
    .select("id, created_at")
    .single();

  if (error) {
    console.error("[supabase.createConsultingRequest] failed", error);
    return { error: error.message };
  }
  return {
    data: { id: (data as any).id, created_at: (data as any).created_at },
  };
}

export async function createIntroRequest(
  input: IntroRequestInput,
): Promise<{ data?: IntroRequestResult; error?: string | null }> {
  const supabase = getSupabase();
  const row = {
    name: input.name ?? null,
    email: input.email ?? null,
    message: input.message ?? null,
  };

  const { data, error } = await supabase
    .from("intro_requests")
    .insert(row)
    .select("id, created_at")
    .single();

  if (error) {
    console.error("[supabase.createConsultingRequest] failed", error);
    return { error: error.message };
  }
  return {
    data: { id: (data as any).id, created_at: (data as any).created_at },
  };
}

export async function listConsulting(): Promise<ConsultingRequest[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("consulting_requests")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data as unknown as ConsultingRequest[];
}

export async function updateConsulting(
  id: UUID,
  patch: Partial<ConsultingRequest>,
): Promise<ConsultingRequest> {
  const supabase = getSupabase();
  const mapped: Record<string, any> = { ...patch };
  if ("estimatedHours" in mapped) {
    mapped.estimated_hours = (mapped as any).estimatedHours;
    delete (mapped as any).estimatedHours;
  }
  const { data, error } = await supabase
    .from("consulting_requests")
    .update(mapped)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data as unknown as ConsultingRequest;
}

export async function getPropertyTemplateKpis(
  id: string,
): Promise<Kpis | null> {
  const sb = getSupabase();

  try {
    if (await propertyExists(id)) {
      const { data, error } = await sb
        .from("properties")
        .select("kpis")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;

      console.info(
        "[supabase] KPIs fetched from properties table:",
        data?.kpis ? "Found" : "Not found",
      );
      return (data?.kpis as Kpis) ?? null;
    } else {
      console.info(
        "[supabase] Property does not exist, fetching from property_template_kpis",
      );
      const { data, error } = await sb
        .from("property_template_kpis")
        .select("kpis")
        .eq("template_id", id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") throw error;
      return (data?.kpis as Kpis) ?? null;
    }
  } catch (error) {
    console.error("[getPropertyTemplateKpis] Failed:", error);
    return null;
  }
}

export async function upsertPropertyTemplateKpis(
  id: string,
  kpis: Kpis,
): Promise<void> {
  const sb = getSupabase();
  const { data: u } = await sb.auth.getUser();
  const updated_by = u?.user?.id ?? null;

  try {
    if (await propertyExists(id)) {
      const { error } = await sb
        .from("properties")
        .update({
          kpis,
        })
        .eq("id", id);

      if (error) throw error;
      console.info("[supabase] KPIs updated in properties table successfully");
    } else {
      const payload = { template_id: id, kpis, updated_by };
      const { error } = await sb
        .from("property_template_kpis")
        .upsert(payload, { onConflict: "template_id" });

      if (error) throw error;
      console.info("[supabase] Template KPIs saved (template) successfully");
    }
  } catch (error) {
    console.error("[upsertPropertyTemplateKpis] Failed:", error);
    throw error;
  }
}

export async function createDeletionRequest(
  input: DeletionRequestInput,
): Promise<{ data?: DeletionRequestResult; error?: string | null }> {
  const supabase = getSupabase();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user || user.id !== input.userId) {
    return { error: "User authentication mismatch. Cannot create request." };
  }

  const token = globalThis.crypto.randomUUID();

  const expires = new Date();
  expires.setHours(expires.getHours() + 1);

  const row = {
    user_id: input.userId,
    token: token,
    expires_at: expires.toISOString(),
    status: "PENDING",
  };

  const { data, error } = await supabase
    .from("deletion_requests")
    .insert(row)
    .select("id, created_at, token, expires_at")
    .single();

  if (error) {
    console.error("[supabase.createDeletionRequest] failed", error);

    if (error.code === "42501") {
      return {
        error: "Row Level Security policy violation. Check permissions.",
      };
    }
    if (error.code === "23503") {
      return { error: "User not found." };
    }
    if (error.code === "23505") {
      return {
        error: "A request with this token already exists. Please try again.",
      };
    }

    return { error: error.message };
  }

  return {
    data: {
      id: data.id,
      created_at: data.created_at,
      token: data.token,
      expires_at: data.expires_at,
    },
  };
}

export type DealStub = Pick<
  Deal,
  | "id"
  | "name"
  | "location"
  | "photoUrl"
  | "purchasePrice"
  | "roomTypes"
  | "createdAt"
  | "updatedAt"
>;

export async function listProjectsSlim(userId: string): Promise<DealStub[]> {
  const supabase = getSupabase();
  if (!userId) return [];

  const { data, error } = await supabase
    .from("projects")
    .select(
      `
      id,
      name,
      created_at,
      updated_at,
      location:kpis->>location,
      photo_url:kpis->>photoUrl,
      purchase_price:kpis->>purchasePrice,
      room_types:kpis->roomTypes,
      room_mix:kpis->roomMix
    `,
    )
    .eq("owner_id", userId)
    .order("created_at", { ascending: false });

  if (error) throw error;

  return (data as any).map((row: any) => ({
    id: row.id,
    name: row.name || "Unnamed Deal",
    location: row.location || "Location not specified",
    photoUrl: row.photo_url || "",
    purchasePrice: parseFloat(row.purchase_price || "0"),
    roomTypes: Array.isArray(row.room_types) ? row.room_types : [],
    roomMix: Array.isArray(row.room_mix) ? row.room_mix : [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  })) as DealStub[];
}
